/**
 * @fileoverview Geometry utility functions for point-in-polygon testing and distance calculations.
 * @module utils/jrgeofuncs
 */

import { DEBUG_CBM, MAP_CONSTANTS, MERCATOR_NYC_DISTANCE_FACTOR } from '../constants.js';
import { loadJsonDataFromPath } from '../utils/jrfuncs.js';










// ==========================================
// Calculating distanced used for clicking on blank area of map and getting block loclabel
// ==========================================

/**
 * Tests if a point is inside a GeoJSON polygon geometry.
 * Uses ray casting algorithm.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {Object} geometry - GeoJSON geometry object (Polygon or MultiPolygon)
 * @returns {boolean} True if point is inside the polygon
 */
export function isPointInPolygonGeometry(px, py, geometry) {
    if (geometry.type === 'Polygon') {
        return isPointInPolygonRing(px, py, geometry.coordinates[0]);
    } else if (geometry.type === 'MultiPolygon') {
        for (const polygon of geometry.coordinates) {
            if (isPointInPolygonRing(px, py, polygon[0])) {
                return true;
            }
        }
    }
    return false;
}


/**
 * Tests if a point is inside a polygon ring using ray casting algorithm.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {Array<Array<number>>} ring - Array of [x, y] coordinates forming the ring
 * @returns {boolean} True if point is inside the ring
 */
export function isPointInPolygonRing(px, py, ring) {
    let inside = false;
    const n = ring.length;

    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = ring[i][0], yi = ring[i][1];
        const xj = ring[j][0], yj = ring[j][1];

        if (((yi > py) !== (yj > py)) &&
            (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }

    return inside;
}


/**
 * Calculates the minimum distance from a point to a GeoJSON polygon geometry.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {Object} geometry - GeoJSON geometry object (Polygon or MultiPolygon)
 * @returns {number} Minimum distance to the polygon
 */
export function distanceToPolygonGeometry(px, py, geometry) {
    let minDistance = Infinity;

    if (geometry.type === 'Polygon') {
        minDistance = distanceToPolygonRing(px, py, geometry.coordinates[0]);
    } else if (geometry.type === 'MultiPolygon') {
        for (const polygon of geometry.coordinates) {
            const distance = distanceToPolygonRing(px, py, polygon[0]);
            if (distance < minDistance) {
                minDistance = distance;
            }
        }
    }

    return minDistance;
}


/**
 * Calculates the minimum distance from a point to a polygon ring.
 * Computes distance to each edge segment and returns the minimum.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {Array<Array<number>>} ring - Array of [x, y] coordinates forming the ring
 * @returns {number} Minimum distance to the ring
 */
export function distanceToPolygonRing(px, py, ring) {
    let minDistance = Infinity;
    const n = ring.length;

    for (let i = 0; i < n - 1; i++) {
        const x1 = ring[i][0], y1 = ring[i][1];
        const x2 = ring[i + 1][0], y2 = ring[i + 1][1];

        const distance = distanceToLineSegment(px, py, x1, y1, x2, y2);
        if (distance < minDistance) {
            minDistance = distance;
        }
    }

    return minDistance;
}


/**
 * Calculates the distance from a point to a line segment.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {number} x1 - Segment start x
 * @param {number} y1 - Segment start y
 * @param {number} x2 - Segment end x
 * @param {number} y2 - Segment end y
 * @returns {number} Distance from point to segment
 */
export function distanceToLineSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
        // Segment is a point
        return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
    }

    // Project point onto line, clamping to segment
    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));

    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;

    return Math.sqrt((px - nearestX) * (px - nearestX) + (py - nearestY) * (py - nearestY));
}


/**
 * Calculates the squared distance between a point and a coordinate array.
 * Uses squared distance to avoid expensive sqrt calculation when only comparing distances.
 * @param {number} px - Point x coordinate
 * @param {number} py - Point y coordinate
 * @param {Array<number>} coords - [x, y] coordinate array
 * @returns {number} Squared distance between the point and coordinates
 */
export function distanceSquaredToCoords(px, py, coords) {
    const dx = coords[0] - px;
    const dy = coords[1] - py;
    return dx * dx + dy * dy;
}













// ==========================================
// zoom extents
// ==========================================



/**
 * Scale an extent (bounding box) by a factor around its center point
 * @param {Array<number>} extent - [minX, minY, maxX, maxY]
 * @param {number} factor - Scale factor (e.g., 0.8 to shrink by 20%, 1.2 to expand by 20%)
 * @returns {Array<number>} Scaled extent [minX, minY, maxX, maxY]
 */
export function scaleExtent(extent, factor) {
    if (factor==1.0) {
        return extent;
    }
    factor = 1.0 / factor;
    
    const [minX, minY, maxX, maxY] = extent;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const halfW = (maxX - minX) * factor / 2;
    const halfH = (maxY - minY) * factor / 2;
    return [
        cx - halfW, cy - halfH,
        cx + halfW, cy + halfH
    ];
}





/**
 * Computes the tile-grid-snapped extent for a Web Mercator (EPSG:3857) tile set.
 * At low zoom levels, individual tiles cover enormous areas, so this function iterates
 * from minZoom upward to find the lowest zoom where the snapped extent doesn't overshoot
 * the original bounds by more than maxOversizeFactor in either dimension.
 * @param {Array<number>} bounds - [minLon, minLat, maxLon, maxLat] in WGS84 degrees
 * @param {number} minZoom - Lowest available zoom level from the tile set
 * @param {number} maxZoom - Highest available zoom level from the tile set
 * @param {number} [maxOversizeFactor=1.5] - Maximum allowed ratio of grid extent to original bounds per axis
 * @returns {Array<number>} Tile-grid-snapped extent [minLon, minLat, maxLon, maxLat] in WGS84 degrees
 */
export function computeTileGridExtent(bounds, minZoom, maxZoom, maxOversizeFactor = 1.5) {
    const boundsWidth = bounds[2] - bounds[0];
    const boundsHeight = bounds[3] - bounds[1];

    // Iterate from low zoom (large tiles) upward to find the lowest zoom
    // where the tile grid doesn't overshoot the bounds by too much
    for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
        const gridExtent = tileGridExtentAtZoom(bounds, zoom);
        const gridWidth = gridExtent[2] - gridExtent[0];
        const gridHeight = gridExtent[3] - gridExtent[1];
        if (gridWidth <= boundsWidth * maxOversizeFactor && gridHeight <= boundsHeight * maxOversizeFactor) {
            return gridExtent;
        }
    }

    // Fallback: return extent at maxZoom (closest to original bounds)
    return tileGridExtentAtZoom(bounds, maxZoom);
}


/**
 * Computes the tile-grid-snapped extent at a specific zoom level.
 * Snaps the bounds outward to tile boundaries for a Web Mercator tile grid.
 * @param {Array<number>} bounds - [minLon, minLat, maxLon, maxLat] in WGS84 degrees
 * @param {number} zoom - Zoom level
 * @returns {Array<number>} Snapped extent [minLon, minLat, maxLon, maxLat] in WGS84 degrees
 */
function tileGridExtentAtZoom(bounds, zoom) {
    const [minLon, minLat, maxLon, maxLat] = bounds;
    const n = Math.pow(2, zoom);

    // Convert lon/lat to tile coordinates at this zoom level
    const minTileX = Math.floor((minLon + 180) / 360 * n);
    const maxTileX = Math.floor((maxLon + 180) / 360 * n);

    // In slippy map tile coords, latitude is inverted (higher lat = lower tile Y)
    const minTileY = Math.floor((1 - Math.log(Math.tan(maxLat * Math.PI / 180) + 1 / Math.cos(maxLat * Math.PI / 180)) / Math.PI) / 2 * n);
    const maxTileY = Math.floor((1 - Math.log(Math.tan(minLat * Math.PI / 180) + 1 / Math.cos(minLat * Math.PI / 180)) / Math.PI) / 2 * n);

    // Convert tile boundaries back to lon/lat
    function tileToLon(x, z) {
        return x / Math.pow(2, z) * 360 - 180;
    }
    function tileToLat(y, z) {
        const npi = Math.PI - 2 * Math.PI * y / Math.pow(2, z);
        return 180 / Math.PI * Math.atan(0.5 * (Math.exp(npi) - Math.exp(-npi)));
    }

    return [
        tileToLon(minTileX, zoom),
        tileToLat(maxTileY + 1, zoom),
        tileToLon(maxTileX + 1, zoom),
        tileToLat(minTileY, zoom)
    ];
}


/**
* Fits the map view to the specified bounds with animation.
* Used on initial reset of map (doesnt care about features just the map tileset itself)
* @param {Array<number>} bounds - Extent array [minLon, minLat, maxLon, maxLat]
*/
export function doFitBounds(map, bounds) {
    const [minLon, minLat, maxLon, maxLat] = bounds;
    const extent = ol.proj.transformExtent(
        [minLon, minLat, maxLon, maxLat],
        MAP_CONSTANTS.PROJECTION.WGS84,
        MAP_CONSTANTS.PROJECTION.WEB_MERCATOR
    );
    map.getView().fit(extent, {
        padding: MAP_CONSTANTS.DEFAULT_PADDING,
        duration: MAP_CONSTANTS.ANIMATION_DURATION_DEFAULT
    });
}







// ==========================================
// geoJson helpers
// ==========================================





/**
 * Extracts features from a GeoJSON object, keeping only specified fields from properties.
 * @param {Object} fullData - GeoJSON object with features array
 * @param {Array<string>} fieldList - List of property field names to keep
 * @param {boolean} flagIncludeGeometry - Whether to include geometry in the output
 * @param {string|null} [optionalProjection] - Source projection for optional coordinate transformation
 * @returns {Array<Object>} Array of extracted feature objects with selected fields
 */
export function geoJsonExtractFeatures(fullData, fieldList, flagIncludeGeometry, optionalProjection) {
    // test
    if (!fullData || !fullData.features) {
        return [];
    }

    let extractedRows = [];

    for (const feature of fullData.features) {
        const row = {};

        // Extract only the specified fields from properties
        if (feature.properties) {
            for (const field of fieldList) {
                if (feature.properties.hasOwnProperty(field)) {
                    row[field] = feature.properties[field];
                }
            }
        }

        // Include geometry if requested
        if (flagIncludeGeometry && feature.geometry) {
            row.geometry = feature.geometry;
        }

        extractedRows.push(row);
    }

    // optional projection transform
    if (optionalProjection) {
        extractedRows = doProjectTransformGeoData(extractedRows, optionalProjection);
    }

    return extractedRows;
}




// ==========================================
// projection transformation helpers
// ==========================================


/**
 * Transforms the geometry field of each row from a source projection to Web Mercator.
 * Skips transformation if the source projection matches the target or is not specified.
 * @param {Array<Object>} rowData - Array of data rows with optional geometry fields
 * @param {string} fromProjection - Source projection identifier (e.g., 'EPSG:2263')
 * @returns {Array<Object>} New array of rows with transformed geometry fields
 */
export function doProjectTransformGeoData(rowData, fromProjection) {
    // Project the geometry field of each row to our standard transformation (Web Mercator)
    const toProjection = MAP_CONSTANTS.PROJECTION.WEB_MERCATOR;

    if (!fromProjection || (toProjection == fromProjection)) {
        // nothing to do
        return rowData;
    }

    const newRowData = rowData.map(row => {
        if (!row.geometry) {
            return row;
        }

        const transformedRow = { ...row };
        if (fromProjection=="negativeVertical") {
            // kludge projection
            transformedRow.geometry = [row.geometry[0], -row.geometry[1]];
        } else {
            transformedRow.geometry = transformGeometry(row.geometry, fromProjection, toProjection);
        }
        return transformedRow;
    });

    if (DEBUG_CBM) {
        console.debug(`DEBUG: Transformed row data from ${fromProjection} resulted in ${newRowData.length} rows.`)
    }
    return newRowData;
}





/**
 * Transforms a GeoJSON geometry object from one projection to another.
 * Supports Point, Polygon, and MultiPolygon geometry types.
 * @param {Object} geometry - GeoJSON geometry object
 * @param {string} fromProjection - Source projection (e.g., 'EPSG:2263')
 * @param {string} toProjection - Target projection (e.g., 'EPSG:3857')
 * @returns {Object} Transformed geometry object
 */
export function transformGeometry(geometry, fromProjection, toProjection) {
    const type = geometry.type;

    if (type === 'Point') {
        return {
            type: 'Point',
            coordinates: ol.proj.transform(geometry.coordinates, fromProjection, toProjection)
        };
    }

    if (type === 'Polygon') {
        return {
            type: 'Polygon',
            coordinates: geometry.coordinates.map(ring =>
                ring.map(coord => ol.proj.transform(coord, fromProjection, toProjection))
            )
        };
    }

    if (type === 'MultiPolygon') {
        return {
            type: 'MultiPolygon',
            coordinates: geometry.coordinates.map(polygon =>
                polygon.map(ring =>
                    ring.map(coord => ol.proj.transform(coord, fromProjection, toProjection))
                )
            )
        };
    }

    // Return unchanged for unsupported geometry types
    console.error(`ERROR: Unsupported geometry type for projection transform: ${type}`);
    return geometry;
}






/**
 * Loads GeoJSON data from a URL path and converts it to row data format.
 * Handles projection transformation if specified.
 * @async
 * @param {string} path - URL path to fetch the GeoJSON data from
 * @param {string} debugOperationLabel - Label for error messages describing the operation
 * @param {string|null} projection - Source projection for coordinate transformation (null to skip)
 * @param {string} sourceName - Identifier for the data source
 * @param {number} [debugSimulateNetworkDelayMs=0] - Milliseconds to delay for debug simulation
 * @returns {Promise<Array<Object>>} Array of row data objects with transformed coordinates
 */
export async function loadGeoJsonRowDataFromPath(path, debugOperationLabel, projection, sourceName, debugSimulateNetworkDelayMs) {
    let data = await loadJsonDataFromPath(path, debugOperationLabel, debugSimulateNetworkDelayMs);
    data = jrConvertGeoJsonFeatures(data, projection, sourceName);
    return data;
}

/**
 * Converts GeoJSON FeatureCollection or plain array data into a flat row array.
 * Extracts features from GeoJSON format and applies optional projection transformation.
 * @param {Object|Array} data - GeoJSON FeatureCollection object or plain array of items
 * @param {string|null} projection - Source projection for coordinate transformation (null to skip conversion)
 * @param {string} [sourceName] - Optional source identifier added as _sourceName field
 * @returns {Array<Object>} Flat array of row data objects
 */
export function jrConvertGeoJsonFeatures(data, projection, sourceName) {
    // Handle both GeoJSON and plain arrays
    if (data.type === 'FeatureCollection' && data.features) {
        // GeoJSON format - extract features and add source type
        data = data.features.map(feature => ({
            ...feature,
            ...(sourceName != null && { _sourceName: sourceName }),
            _properties: feature.properties || {}
        }));
    } else if (Array.isArray(data)) {
        // Plain array format
        data =  data.map(item => ({
            ...item,
            ...(sourceName != null && { _sourceName: sourceName }),
        }));
    } else {
        console.error(`ERROR: Unexpected data format for ${sourceName}`);
        return [];
    }

    // do a projection transformation if appropriate
    if (projection) {
        data = doProjectTransformGeoData(data, projection);
    }
    return data;
}













/**
 * Converts a distance in Mercator coordinate units to a human-readable string.
 * Automatically selects appropriate units (meters/km or feet/miles) based on magnitude.
 * @param {number} distanceInUnits - Distance in native Mercator coordinate units or pixels if in raster mode
 * @param {string} targetUnit - Unit system: 'metric' for meters/km, 'imperial' for feet/miles
 * @param {boolean} flagVerbose - If true, uses full unit names (e.g., 'kilometers'); if false, abbreviations (e.g., 'km')
 * @returns {string} Formatted distance string (e.g., '150 meters', '0.5 km', '300 ft', '1.2 miles')
 */
export function convertDistanceGeometryUnitsToNiceString(distanceInUnits, targetUnit, flagVerbose, rasterScale) {
    // distInMeters represents distance as a number of meters
    // return a nice human readable string that converts distanceInMeters to targetUnit
    // flagVerbose can choose between a short string and a long verbose one
    // targetUnits may be "metric" or "imperial"
    // if targetUnit is "metric" then we will report EITHER a rounded number of "meters" or a number of "kilometers" formatted with 1 decimal place
    // if tergetUnit is "imperial" then we will report EITHER a rounded number of "feed" or a number of "miles" formatted with 1 decimal place
    // for "metric, if flagVerbose then we will report "meters" and "kilometers", otherwise "m" and "km"
    // for "imperial", if flagVerboce then we will report "feet" and "miles" otherwise "ft" and "mi"
    // use plurals where appropriate.  distance will never be negative but may be 0.
    let distanceInMeters;

    // Raster mode: distances are in pixels, report directly
    if (rasterScale==-1) {
        // no raster scale, just say pixels
        const pixels = Math.round(distanceInUnits);
        const unit = flagVerbose ? (pixels === 1 ? 'pixel' : 'pixels') : 'px';
        return `${pixels} ${unit}`;
    } else if (rasterScale) {
        // convert to pixel scale, then drop down
        distanceInMeters = distanceInUnits * rasterScale;
    } else {
        distanceInMeters = MERCATOR_NYC_DISTANCE_FACTOR * distanceInUnits;
    }

    const minFractionBeforeLargeUnits = 0.499;
    const FEET_PER_METER = 3.28084;
    const FEET_PER_MILE = 5280;
    const METERS_PER_KM = 1000;

    if (targetUnit === 'metric') {
        const km = distanceInMeters / METERS_PER_KM;
        if (km >= minFractionBeforeLargeUnits) {
            let kmFormatted = km.toFixed(1);
            if (km < 1) {
                kmFormatted = '0' + kmFormatted.replace(/^0/, '');
            }
            const unit = flagVerbose ? (km === 1 ? 'kilometer' : 'kilometers') : 'km';
            return `${kmFormatted} ${unit}`;
        } else {
            const meters = Math.round(distanceInMeters);
            const unit = flagVerbose ? (meters === 1 ? 'meter' : 'meters') : 'm';
            return `${meters} ${unit}`;
        }
    } else {
        // imperial
        const distanceInFeet = distanceInMeters * FEET_PER_METER;
        const miles = distanceInFeet / FEET_PER_MILE;
        if (miles >= minFractionBeforeLargeUnits) {
            let milesFormatted = miles.toFixed(1);
            if (miles < 1) {
                milesFormatted = '0' + milesFormatted.replace(/^0/, '');
            }
            const unit = flagVerbose ? (miles === 1 ? 'mile' : 'miles') : 'mi';
            return `${milesFormatted} ${unit}`;
        } else {
            const feet = Math.round(distanceInFeet);
            const unit = flagVerbose ? (feet === 1 ? 'foot' : 'feet') : 'ft';
            return `${feet} ${unit}`;
        }
    }
}



/**
 * Calculates the Euclidean distance between two items with GeoJSON Point geometries.
 * @param {Object} item1 - First item with geometry property containing a GeoJSON Point
 * @param {Object} item2 - Second item with geometry property containing a GeoJSON Point
 * @returns {number|null} Distance in native coordinate units, or null if geometries are invalid
 */
export function distanceBetweenTwoItems(item1, item2) {
    const coords1 = getPointCoordinates(item1);
    const coords2 = getPointCoordinates(item2);

    if (!coords1 || !coords2) {
        return null;
    }

    const dx = coords2[0] - coords1[0];
    const dy = coords2[1] - coords1[1];
    return Math.sqrt(dx * dx + dy * dy);
}


/**
 * Extracts coordinates from an item's GeoJSON Point geometry.
 * @param {Object} item - Item with geometry property
 * @returns {Array<number>|null} [x, y] coordinates or null if not a valid Point
 */
function getPointCoordinates(item) {
    if (!item || !item.geometry) {
        return null;
    }

    const geometry = item.geometry;
    if (geometry.type !== 'Point' || !Array.isArray(geometry.coordinates)) {
        return null;
    }

    return geometry.coordinates;
}


/**
 * Whether an item has a GeoJSON Point to measure distances from. Many directory entries have none
 * (an unlisted address), and such an item must not become the anchor: every distance to it is
 * null (second code study 12.3).
 * @param {Object} item - Item with an optional geometry property
 * @returns {boolean}
 */
export function itemHasPointGeometry(item) {
    return (getPointCoordinates(item) !== null);
}



/**
 * Converts a distance from native Mercator coordinate units to feet.
 * Uses the NYC-specific Mercator distance correction factor.
 * @param {number} dist - Distance in native Mercator units
 * @returns {number} Distance in feet
 */
export function convertNativeMercaturUnitsToFeet(dist) {
    // Convert from native Mercator units to meters, then to feet
    const FEET_PER_METER = 3.28084;
    const distanceInMeters = dist * MERCATOR_NYC_DISTANCE_FACTOR;
    return distanceInMeters * FEET_PER_METER;
}

/**
 * Converts a distance from feet to native Mercator coordinate units.
 * Uses the NYC-specific Mercator distance correction factor.
 * @param {number} dist - Distance in feet
 * @returns {number} Distance in native Mercator units
 */
export function convertFeetToNativeMercaturUnits(dist) {
    // Convert from feet to meters, then to native Mercator units
    const FEET_PER_METER = 3.28084;
    const distanceInMeters = dist / FEET_PER_METER;
    return distanceInMeters / MERCATOR_NYC_DISTANCE_FACTOR;
}

